import React, { useEffect, useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Plus, Trash2, Star } from "lucide-react";
import { api } from "@/services/api";
import InvoiceDocument from "@/components/InvoiceDocument";
import { buildFromStaticPreview } from "@/components/invoice";

const previewInvoice = {
  invoice_number: "INV-1001",
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10),
  reverse_charge: false,
  transport: "Road",
  e_way_bill_no: "1234567890",
  irn_number: "",
  status: "pending",
  items: [
    { id: "a", item_name: "Website Design", description: "Landing page + docs", quantity: 1, unit: "pcs", price: 25000, hsn_sac: "998311", amount: 25000 },
    { id: "b", item_name: "Copywriting", description: "3 pages", quantity: 3, unit: "hrs", price: 3000, hsn_sac: "998313", amount: 9000 },
  ],
  subtotal: 34000, discount: 0, shipping: 0, cgst: 3060, sgst: 3060, igst: 0, total: 40120,
  notes: "Delivery within 5 business days.",
  terms: "Payment due within 7 days of receipt.",
};

const previewCustomer = { name: "Acme Retail Pvt Ltd", company: "Acme Group", address: "42, Baker Street", city: "Mumbai", state: "MH", country: "India", email: "billing@acme.io", phone: "+91 90000 00000", gst: "27ABCDE1234F1Z5" };

export default function TemplatesPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: orgs = [] } = useQuery({ queryKey: ["orgs"], queryFn: async () => (await api.get("/organizations")).data });
  const [orgId, setOrgId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => { if (orgs.length && !orgId) setOrgId(orgs[0].id); }, [orgs, orgId]);

  const org = orgs.find((o) => o.id === orgId);
  const { data: templates = [] } = useQuery({
    queryKey: ["templates", orgId],
    queryFn: async () => (await api.get(`/organizations/${orgId}/templates`)).data,
    enabled: !!orgId,
  });

  const { data: template } = useQuery({
    queryKey: ["template", selectedTemplateId],
    queryFn: async () => (await api.get(`/templates/${selectedTemplateId}`)).data,
    enabled: !!selectedTemplateId,
  });

  const [form, setForm] = useState(null);
  const [originalTemplate, setOriginalTemplate] = useState(null);
  useEffect(() => {
    if (template) {
      // Reset form with template data to prevent duplication
      // Create a clean copy to avoid reference issues
      const templateCopy = JSON.parse(JSON.stringify(template));
      setForm(templateCopy);
      setOriginalTemplate(templateCopy);
      setIsCreating(false);
    } else {
      setForm(null);
      setOriginalTemplate(null);
    }
  }, [template]);

  const create = useMutation({
    mutationFn: async () => {
      const newTemplate = {
        name: "New Template",
        layout_type: "gst",
        primary_color: "#002FA7",
        secondary_color: "#0A0A0B",
        font_family: "Manrope",
        logo_position: "left",
        header_style: "modern",
        show_bank_details: true,
        show_qr: true,
        show_signature: true,
        show_terms: true,
        show_notes: true,
        show_powered_by: true,
        invoice_title: "TAX INVOICE",
        item_columns: ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"],
      };
      return (await api.post(`/organizations/${orgId}/templates`, newTemplate)).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["templates", orgId] });
      setSelectedTemplateId(data.id);
      setIsCreating(true);
      toast.success("Template created");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (isCreating) {
        return (await api.put(`/templates/${selectedTemplateId}`, form)).data;
      }
      return (await api.put(`/templates/${selectedTemplateId}`, form)).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["templates", orgId] });
      qc.invalidateQueries({ queryKey: ["template", selectedTemplateId] });
      // Update originalTemplate to the saved state so hasChanges becomes false
      setOriginalTemplate(JSON.parse(JSON.stringify(data)));
      toast.success("Template saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const deleteTemplate = useMutation({
    mutationFn: async (templateId) => {
      return (await api.delete(`/templates/${templateId}`)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["templates", orgId] });
      if (selectedTemplateId) {
        setSelectedTemplateId(null);
        setForm(null);
      }
      toast.success("Template deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const update = (patch) => {
    const updatedForm = { ...form, ...patch };
    // If layout_type is changing, update item_columns to match the new layout
    if (patch.layout_type && form?.layout_type && patch.layout_type !== form.layout_type) {
      updatedForm.item_columns = ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"];
    }
    setForm(updatedForm);
  };

  // Memoize template props for PDF preview to prevent unnecessary re-renders
  const templateProps = useMemo(() => ({
    layout_type: form?.layout_type,
    primary_color: form?.primary_color,
    secondary_color: form?.secondary_color,
    font_family: form?.font_family,
    footer_text: form?.footer_text,
    invoice_title: form?.invoice_title,
    show_powered_by: form?.show_powered_by,
    show_bank_details: form?.show_bank_details,
    show_qr: form?.show_qr,
    show_signature: form?.show_signature,
    show_terms: form?.show_terms,
    show_notes: form?.show_notes,
    item_columns: form?.item_columns,
    logo_position: form?.logo_position,
    header_style: form?.header_style,
  }), [form?.layout_type, form?.primary_color, form?.secondary_color, form?.font_family, form?.footer_text, form?.invoice_title, form?.show_powered_by, form?.show_bank_details, form?.show_qr, form?.show_signature, form?.show_terms, form?.show_notes, form?.item_columns, form?.logo_position, form?.header_style]);

  const previewModel = useMemo(() => {
    return buildFromStaticPreview({
      invoice: previewInvoice,
      organization: org,
      customer: previewCustomer,
      template: templateProps,
      copyType: "original",
    });
  }, [org, templateProps]);

  // Check if form has changed from original template
  const hasChanges = useMemo(() => {
    if (!form || !originalTemplate) return false;
    // Compare all relevant fields
    const fieldsToCompare = [
      'name', 'layout_type', 'primary_color', 'secondary_color', 'font_family',
      'logo_position', 'header_style', 'footer_text', 'show_bank_details',
      'show_qr', 'show_signature', 'show_terms', 'show_notes', 'show_powered_by', 'invoice_title', 'item_columns'
    ];
    return fieldsToCompare.some(field => {
      const formValue = form[field];
      const originalValue = originalTemplate[field];
      // Handle array comparison for item_columns
      if (field === 'item_columns') {
        return JSON.stringify(formValue) !== JSON.stringify(originalValue);
      }
      return formValue !== originalValue;
    });
  }, [form, originalTemplate]);

  if (!orgs.length) {
    return <div className="text-slate-500" data-testid="no-orgs-message">Create an organization first to customize its template.</div>;
  }

  return (
    <div>
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Brand</div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter">Templates.</h1>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <select data-testid="template-org-select" value={orgId} onChange={(e) => setOrgId(e.target.value)} className="border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm">
            {orgs.map((o) => (<option key={o.id} value={o.id}>{o.organization_name}</option>))}
          </select>
          <button data-testid="create-template-button" onClick={() => create.mutate()} disabled={create.isPending} className="inline-flex items-center justify-center gap-2 border border-slate-300 dark:border-slate-700 px-4 py-2.5 rounded-sm hover:border-slate-900 dark:hover:border-white text-sm">
            <Plus className="w-4 h-4" /> {create.isPending ? "Creating…" : "New Template"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-sm p-4">
          <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-4">Your templates</div>
          <div className="space-y-2">
            {templates.map((tpl) => (
              <div
                key={tpl.id}
                data-testid={`template-card-${tpl.id}`}
                onClick={() => setSelectedTemplateId(tpl.id)}
                className={`p-3 border rounded-sm cursor-pointer transition-colors ${
                  selectedTemplateId === tpl.id 
                    ? "border-[#002FA7] bg-[#002FA7]/5" 
                    : "border-slate-200 dark:border-slate-700 hover:border-slate-400"
                }`}
              >
                <div className="font-medium">{tpl.name}</div>
                <div className="text-xs text-slate-500 mt-1">{tpl.primary_color} • {tpl.font_family}</div>
              </div>
            ))}
            {templates.length === 0 && (
              <div className="text-slate-500 text-sm py-4">No templates yet. Create one to get started.</div>
            )}
          </div>
        </div>

        {/* Template Editor */}
        {form && (
          <div className="border border-slate-200 dark:border-slate-800 rounded-sm p-6 space-y-4 lg:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <input
                  data-testid="template-name"
                  value={form.name}
                  onChange={(e) => update({ name: e.target.value })}
                  className="text-xl font-bold bg-transparent border-b border-slate-300 dark:border-slate-700 focus:border-[#002FA7] outline-none pb-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="delete-template-button"
                  onClick={() => deleteTemplate.mutate(form.id)}
                  disabled={deleteTemplate.isPending}
                  className="inline-flex items-center gap-2 text-sm px-3 py-2 border border-red-300 dark:border-red-700 text-red-600 rounded-sm hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
                <button
                  data-testid="save-template-button"
                  onClick={() => save.mutate()}
                  disabled={save.isPending || !hasChanges}
                  className="bg-[#002FA7] text-white px-5 py-2.5 rounded-sm hover:bg-[#001F73] disabled:opacity-50 font-semibold"
                >
                  {save.isPending ? "Saving…" : "Save Template"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <label>
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Invoice title</span>
                <input data-testid="template-title" value={form.invoice_title} onChange={(e) => update({ invoice_title: e.target.value })} className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm" />
              </label>
              <label>
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Font family</span>
                <select data-testid="template-font" value={form.font_family} onChange={(e) => update({ font_family: e.target.value })} className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm">
                  {["Manrope","Inter","Open Sans","Helvetica","Georgia","Courier"].map((f)=>(<option key={f}>{f}</option>))}
                </select>
              </label>
              <label>
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Primary color</span>
                <input type="color" data-testid="template-primary" value={form.primary_color} onChange={(e) => update({ primary_color: e.target.value })} className="mt-1 w-full h-10 border border-slate-300 dark:border-slate-700 bg-transparent rounded-sm" />
              </label>
              <label>
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Secondary color</span>
                <input type="color" data-testid="template-secondary" value={form.secondary_color} onChange={(e) => update({ secondary_color: e.target.value })} className="mt-1 w-full h-10 border border-slate-300 dark:border-slate-700 bg-transparent rounded-sm" />
              </label>
              {/* <label>
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Logo position</span>
                <select data-testid="template-logo-pos" value={form.logo_position} onChange={(e) => update({ logo_position: e.target.value })} className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm">
                  {["left","right"].map((v)=>(<option key={v}>{v}</option>))}
                </select>
              </label> */}
              <label>
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Layout type</span>
                <select data-testid="template-layout" value={form.layout_type || "gst"} onChange={(e) => update({ layout_type: e.target.value })} className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm">
                  <option value="gst">GST (Indian Style)</option>
                  <option value="tally">Tally (Traditional Indian)</option>
                  {/* <option value="classic">Classic (Side-by-side)</option> */}
                  {/* <option value="minimal">Minimal (Clean & Simple)</option> */}
                  {/* <option value="modern">Modern (Bold & Contemporary)</option> */}
                </select>
              </label>
              {/* <label>
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Footer text</span>
                <input data-testid="template-footer" value={form.footer_text} onChange={(e) => update({ footer_text: e.target.value })} className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm" />
              </label> */}
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.show_powered_by !== false} onChange={(e) => update({ show_powered_by: e.target.checked })} />
                Show "Powered by BillForge"
              </label>
            </div>
            {/* <div className="grid grid-cols-2 gap-2 pt-2">
              {[["show_bank_details","Show bank"],["show_qr","Show QR"],["show_signature","Show signature"],["show_terms","Show terms"],["show_notes","Show notes"]].map(([k,label]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`template-toggle-${k}`} checked={!!form[k]} onChange={(e) => update({ [k]: e.target.checked })} />
                  {label}
                </label>
              ))}
            </div> */}

            <div className="pt-4">
              <div className="uppercase text-[10px] tracking-[0.2em] text-slate-500 mb-3">Invoice Item Columns</div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { key: "item_name", label: "Item Name" },
                  { key: "description", label: "Description" },
                  { key: "hsn_sac", label: "HSN/SAC" },
                  { key: "quantity", label: "Quantity" },
                  { key: "unit", label: "Unit" },
                  { key: "price", label: "Price" },
                  { key: "amount", label: "Amount" },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(Array.isArray(form.item_columns) ? form.item_columns : ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"]).includes(key)}
                      onChange={(e) => {
                        const defaultOrder = ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"];
                        const current = Array.isArray(form.item_columns) ? form.item_columns : ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"];
                        if (e.target.checked) {
                          // Only add if not already present
                          if (!current.includes(key)) {
                            // Insert at the correct position based on default order
                            const targetIndex = defaultOrder.indexOf(key);
                            let insertIndex = current.length;
                            
                            // Find the correct position to insert by comparing with existing columns
                            for (let i = 0; i < current.length; i++) {
                              const currentIndex = defaultOrder.indexOf(current[i]);
                              if (currentIndex > targetIndex) {
                                insertIndex = i;
                                break;
                              }
                            }
                            
                            const updated = [...current];
                            updated.splice(insertIndex, 0, key);
                            update({ item_columns: updated });
                          }
                        } else {
                          update({ item_columns: current.filter((c) => c !== key) });
                        }
                      }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div>
              <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-3">Live preview</div>
              <div className="overflow-auto bg-slate-100 dark:bg-slate-900 p-4 rounded-sm">
                <div style={{ width: '100%', maxWidth: '794px', margin: '0 auto' }}>
                  <InvoiceDocument 
                    model={previewModel}
                    showToolbar={false}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {!form && (
          <div className="border border-slate-200 dark:border-slate-800 rounded-sm p-6 lg:col-span-2 flex items-center justify-center text-slate-500">
            Select a template to edit or create a new one
          </div>
        )}
      </div>
    </div>
  );
}
